import { queryClient } from '~/api/queryClient';
import { queryKeys } from '~/api/queryKeys';
import { fetchNavigation } from '~/api/services/navigation.service';
import { UNGUARDED_ROUTES } from '~/constants/config/unguarded-routes';
import { useAuthStore } from '~/store/auth.store';
import type { NavigationResponse } from '~/types/navigation.types';
import { TENANT_ADMIN_ROLES } from '~/utils/guards';
import { safeRedirectTarget } from '~/utils/safe-redirect';

const STORAGE_KEY = 'post_login_redirect';

/**
 * Persist a redirect target across the OAuth round-trip. Search params on
 * /login do not survive the IdP bounce, so we stash the deep-link in
 * sessionStorage right before navigating to the SSO start URL. Always clears
 * any prior entry first so a leftover stash from a previous failed flow
 * cannot bleed into the next attempt — and so SSO without a deep-link
 * starts from a clean slate.
 */
export function stashPostLoginRedirect(target: string | null | undefined): void {
  try {
    sessionStorage.removeItem(STORAGE_KEY);
    const safe = safeRedirectTarget(target ?? null);
    if (safe) sessionStorage.setItem(STORAGE_KEY, safe);
  } catch {
    // sessionStorage unavailable — silently no-op; user will land on '/'.
  }
}

/**
 * Reads + clears the stashed redirect. Single-shot to guarantee a stale
 * entry can never bleed into a later login. Returns null if absent or unsafe.
 */
export function consumePostLoginRedirect(): string | null {
  let raw: string | null = null;
  try {
    raw = sessionStorage.getItem(STORAGE_KEY);
    sessionStorage.removeItem(STORAGE_KEY);
  } catch {
    return null;
  }
  return safeRedirectTarget(raw);
}

/**
 * True when the path matches an UNGUARDED_ROUTES entry (exact or as a parent
 * segment). Mirrors ScreenGuard's bypass list so the resolver agrees with
 * runtime guard behaviour.
 */
function isUnguarded(pathname: string): boolean {
  return UNGUARDED_ROUTES.some((route) => pathname === route || pathname.startsWith(`${route}/`));
}

/**
 * True when the path matches any nav entry the backend returned for this
 * user. Uses prefix-match on the exact list of paths surfaced by
 * /screens/navigation — same logic as useScreenAccess.isPathAllowed.
 */
function isAllowedByNavigation(pathname: string, nav: NavigationResponse): boolean {
  const allowed: string[] = [];
  for (const group of nav.groups) {
    if (group.path) allowed.push(group.path);
    for (const item of group.items) {
      if (item.path) allowed.push(item.path);
      for (const child of item.items) {
        if (child.path) allowed.push(child.path);
      }
    }
  }
  return allowed.some((entry) => pathname === entry || pathname.startsWith(`${entry}/`));
}

/**
 * Resolves the post-login destination. Returns a safe deep-link the user
 * is allowed to access, or '/' as a fallback. Tenant/super admins skip the
 * navigation check; UNGUARDED_ROUTES skip it too. Network failure on the
 * navigation fetch falls back to '/'.
 */
export async function resolvePostLoginRedirect(
  rawTarget: string | null | undefined,
): Promise<string> {
  const target = safeRedirectTarget(rawTarget ?? null);
  if (!target) return '/';

  const { user, isImpersonating } = useAuthStore.getState();
  if (!user) return '/';

  // Admins bypass screen-level checks (mirrors ScreenGuard).
  if (user.isSuperAdmin) return target;
  const upperRole = user.roleCode.toUpperCase();
  if (TENANT_ADMIN_ROLES.includes(upperRole as (typeof TENANT_ADMIN_ROLES)[number])) {
    return target;
  }

  const pathname = new URL(target, window.location.origin).pathname;
  if (isUnguarded(pathname)) return target;

  try {
    const nav = await queryClient.ensureQueryData({
      queryKey: queryKeys.navigation.menu(user.activeTenantId, user.id, isImpersonating),
      queryFn: fetchNavigation,
    });
    if (isAllowedByNavigation(pathname, nav)) return target;
  } catch {
    // Navigation API failed — safest to land on '/'.
  }

  return '/';
}
